/*
  TOPのHow to Useセクションの動作です。
  スマホ画面内の動画と、イラストが連動するようにしています。
  このセクションが画面内に入ってくるまで動画が再生されないようにしています。

  How To Use クラス
    各スライダーの同期をとっている箇所
    各スライダー(以下参照)ごとのクラスを初期化し、双方の同期をとっています。

  IllusSlider クラス
    ただのSwiperの初期化です

  MovieSlider クラス
    動画再生の進捗などに応じたスライダーの動作
    PC時のナビゲーション内の数値を変更する動作
    再生中動画の残り時間ゲージの表示動作
    動作再生のタイミングの操作
  ...などをしています。てんこ盛り

  ===== 動画を差し替える方へ =====
  動画下の残り時間ゲージは、動画の長さを取っているので
  とくに変更する必要は無いと思います。
  CSS調整だけお願いします。

  パ
*/

import 'intersection-observer';
import IntersectionObserver from 'intersection-observer-polyfill/dist/IntersectionObserver.js';
import Swiper from 'swiper/js/swiper.min.js';
import { gsap, TweenLite, CSSPlugin, Power4 } from 'gsap/all';

gsap.registerPlugin(CSSPlugin);

export default class HowToUse {
  constructor() {
      this.illustSlider = new IllustSlider();
      this.movieSlider = new MovieSlider();
      this.handleEvents();

  }

  handleEvents() {
    this.illustSlider.swiper.on('slideChange', () => {
      this.movieSlider.swiper.slideToLoop(this.illustSlider.swiper.realIndex);
    });

    this.movieSlider.swiper.on('slideChange', () => {
      this.illustSlider.swiper.slideToLoop(this.movieSlider.swiper.realIndex);
    });
  }
}

class IllustSlider {
  constructor() {
    this.swiper = new Swiper('#htu_illust', {
      loop: true,
      pagination: {
        el: '.illust-pagination',
        type: 'bullets',
        clickable: true
      },
    });
  }
}

class MovieSlider {
  constructor() {
    const self = this;
    this.section = document.querySelector('.howtouse');
    this.videos = document.querySelectorAll('.usage-movie__video');
    this.progressUI = document.querySelector('#time_progress');
    this.navigationCharPrev = document.getElementById('prev_slide_number');
    this.navigationCharNext = document.getElementById('next_slide_number');

    this.swiper = new Swiper('#htu_movie', {
      loop: true,
      navigation: {
        prevEl: '.movie-navigation-prev',
        nextEl: '.movie-navigation-next'
      },
      pagination: {
        el: '.movie-pagination',
        type: 'custom',
        currentClass: '.movie-pagination-current',
        renderCustom(swiper, current, total) {
          let currentNumber = current;
          let prevNumber;
          let nextNumber;

          if (currentNumber < 10) {
            currentNumber = `0${current}`;
          }

          // Navigation 数値の更新
          // prev
          prevNumber = current - 1;
          if (current === 1) {
            prevNumber = total < 10 ? `0${total}` : `${total}`;
          } else {
            prevNumber = prevNumber < 10 ? `0${prevNumber}` : `${prevNumber}`;
          }
          self.navigationCharPrev.textContent = prevNumber;

          // next
          nextNumber = current + 1;
          if (current === total) {
            nextNumber = '01';
          } else {
            nextNumber = nextNumber < 10 ? `0${nextNumber}` : `${nextNumber}`;
          }
          self.navigationCharNext.textContent = nextNumber;

          return currentNumber;
        }
      },
    });

    this.ioOptions = {
      root: null,
      rootMargin: '0% 0% -25% 0%',
      threshold: [0, 0.25, 0.5, 0.75, 1.0]
    };

    this.io = new IntersectionObserver(this.onIntersect.bind(this), this.ioOptions);
    this.io.observe(this.section);
    this.handleEvents();
  }

  onIntersect(entries) {
    if (entries[0].isIntersecting) {
      this.initialize();
      this.io && this.io.disconnect();
      this.io = null;
    }
  }

  initialize() {
    this.videos[0].play();
    this.progressUpdate(this.videos[0]);
  }

  handleEvents() {
    // 各動画が最後まで再生されたとき
    for (let i = 0; i < this.videos.length; i++) {
      this.videos[i].addEventListener('ended', () => {
        // プログレスバーの停止
        cancelAnimationFrame(this.progressGage);
        if (i === this.videos.length - 1) return;
        this.videos[i].currentTime = 0;
        this.swiper.slideNext();
      });
    }

    //
    // スライド時の動作
    //
    this.swiper.on('slidePrevTransitionStart', () => {
      // プログレスバーの停止
      cancelAnimationFrame(this.progressGage);
      let toSlide = this.swiper.realIndex;
      this.progressSwitch(this.videos[toSlide]);

      if (toSlide >= this.videos.length - 1) {
        toSlide = 0;
        this.videos[toSlide].pause();
        return;
      }
      this.videos[toSlide + 1].pause();

      this.spRemoveProgressStyle();
    });

    this.swiper.on('slideNextTransitionStart', () => {
      // プログレスバーの停止
      cancelAnimationFrame(this.progressGage);
      let toSlide = this.swiper.realIndex;
      this.progressSwitch(this.videos[toSlide]);

      if (toSlide === 0) {
        toSlide = this.videos.length - 1;
        this.videos[toSlide].pause();
        return;
      }
      this.videos[toSlide - 1].pause();

      this.spRemoveProgressStyle();
    });

    this.swiper.on('slideChangeTransitionEnd', () => {
      const video = this.videos[this.swiper.realIndex];
      if (video === true) {
        video.currentTime = 0;
      }
      video.play();

      requestAnimationFrame(this.progressUpdate.bind(this, video));
    });
  }

  progressSwitch(video) {
    const duration = video.duration;
    const currentTime = video.currentTime;
    if(window.innerWidth >= 769) {
      TweenLite.to(this.progressUI, .7, { scaleX: currentTime / duration, ease: Power4.easeOut });
    } else {
      TweenLite.to(this.progressUI, .7, { rotate: currentTime / duration, ease: Power4.easeOut });
    }
  }

  // ゲージアップデート関数
  progressUpdate(video) {
    const targetVideo = video;
    const duration = targetVideo.duration;
    const currentTime = targetVideo.currentTime;
    const progress = currentTime / duration;

    document.querySelector('.usage-movie__progress').classList.add('isAnimation')
    document.querySelector('.time-progress__sp-left').setAttribute('style', 'animation-duration:' + duration + 's')
    document.querySelector('.time-progress__sp-right').setAttribute('style', 'animation-duration:' + duration + 's')

    this.progressGage = requestAnimationFrame(this.progressUpdate.bind(this, targetVideo));
  }

  spRemoveProgressStyle() {
    document.querySelector('.usage-movie__progress').classList.remove('isAnimation')
  }
}